/**
 * "Release" gadget (Ship): what changed since the last Publish (the newest version a Publish saved, then the changes
 * since it: commits, files, services, who), with Publish (the header's Publish dialog, opened with `flow:open-publish`
 * as the Quality tab does) for an editor. Never published: says so. A viewer sees it all but not Publish.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { ago } from '../../../utils/blameModel';
import { changesSummary } from '../../../utils/homeGadgets/ship';
import GadgetCard, { GadgetBody, TabLink } from './GadgetCard';
import { useRelease } from './sources';

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Opens the header's Publish dialog (UnifiedTopBar listens). */
export const openPublish = () => window.dispatchEvent(new CustomEvent('flow:open-publish'));

export default function ReleaseGadget({ projectId, canEdit = false, onOpenVersions = null }) {
  const { data, error, loading } = useRelease(projectId);
  const publish = data?.publish || null;
  const summary = changesSummary(data?.changes || []);
  const publishButton = canEdit ? <button className="ph-btn ph-btn-small" data-testid="release-publish" onClick={openPublish} type="button">Publish…</button> : null;
  return (
    <GadgetCard links={<TabLink onClick={onOpenVersions}>Versions</TabLink>} meta={publish ? `published ${ago(publish.createdAt)}` : null} testId="release-gadget" title="Release">
      <GadgetBody
        data={data}
        empty={false}
        error={error}
        errorText="The versions could not be read just now."
        loading={loading}
        loadingText="Reading what changed since the last Publish…"
      >
        {() => (
          <>
            {!publish ? (
              <p className="ph-quiet" data-testid="release-never">Not published yet.</p>
            ) : summary.commits === 0 ? (
              <p className="ph-quiet" data-testid="release-none">Nothing changed since {publish.name || 'the last Publish'}.</p>
            ) : (
              <ul className="gd-rows" data-testid="release-changes">
                <li className="gd-row"><strong className="gd-num">{plural(summary.commits, 'change')}</strong><span className="gd-grow ph-muted">{plural(summary.files, 'file')} since {publish.name || 'the last Publish'}</span></li>
                {summary.services.length > 0 && <li className="gd-row"><span className="ph-muted">Services:</span><span className="gd-grow">{summary.services.join(', ')}</span></li>}
                {summary.people.length > 0 && <li className="gd-row"><span className="ph-muted">By:</span><span className="gd-grow">{summary.people.join(', ')}</span></li>}
              </ul>
            )}
            {publishButton && <div className="gd-actions">{publishButton}</div>}
          </>
        )}
      </GadgetBody>
    </GadgetCard>
  );
}

ReleaseGadget.propTypes = { canEdit: PropTypes.bool, onOpenVersions: PropTypes.func, projectId: PropTypes.string.isRequired };
